import type { JsonDate } from "@aprao/common/src/shapes/common.js";
import type { ShapeAppraisalDocument } from "@aprao/model/src/shapes/ShapeAppraisal.js";
import type { ShapeProject } from "@aprao/model/src/shapes/ShapeProject.js";
import {
  createAppraisalFacades,
  defaultPriceManagerFactory,
} from "@aprao/model/src/stores/createAppraisalFacades.js";
import { createOtherCostsRelatives } from "@aprao/model/src/stores/createOtherCostsRelatives.js";
import { withCostsPredicate } from "@aprao/model/src/stores/OtherCostsGroupFacade.js";
import { css } from "@emotion/react";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useI18n } from "../common/ApraoI18n.js";
import { getWebEnvDetails } from "../common/getWebEnvDetails.js";
import {
  createNumberUnits,
  NumberUnitsContext,
} from "../common/useNumberUnits.js";
import { completionLatch } from "./completionLatch.js";
import { FormattedPdfDocument } from "./FormattedPdfDocument.js";
import { Page } from "./Page.js";
import { BuildCost } from "./sections/BuildCost.js";
import { CoverPage, LocationMap } from "./sections/CoverPage.js";
import { Finance } from "./sections/Finance.js";
import { OtherCosts } from "./sections/OtherCosts.js";
import { RevenueBreakdown } from "./sections/revenue/RevenueBreakdown.js";
import { SitePurchase } from "./sections/SitePurchase.js";
import { SummaryFinancial } from "./sections/SummaryFinancial.js";
import { SensitivitySection } from "./SensitivitySection.js";
import { formatReportTitle } from "./utils.js";

const envDetails = getWebEnvDetails();

export interface AppraisalReportProps {
  className?: string;
  project: ShapeProject;
  appraisalDocument: ShapeAppraisalDocument;
  updatedDate: JsonDate;
  mapImageUrl: string | null;
  logoUrl: string | null;
  isOperatedAssetSensitivityAnalysesEnabled: boolean;
  onReady?: () => void;
  onError?: (error: unknown) => void;
}

export const AppraisalReport = observer(function AppraisalReport(
  props: AppraisalReportProps,
) {
  const {
    project,
    appraisalDocument,
    updatedDate,
    mapImageUrl,
    logoUrl,
    isOperatedAssetSensitivityAnalysesEnabled,
    onReady = () => {
      // noop
    },
    onError = () => {
      // noop
    },
    ...others
  } = props;

  const reportTitle = formatReportTitle(project.document, appraisalDocument);

  const {
    documentFacade,
    taxConfigurationFacade,
    taxFacade,
    priceManager,
    residualFacade,
    cashflowFacade,
    financeFacade,
    flowFacade,
  } = createAppraisalFacades({
    document: appraisalDocument,
    taxConfiguration: project.salesTaxConfiguration,
    priceManagerFactory: defaultPriceManagerFactory,
  });

  const otherCostsRelatives = createOtherCostsRelatives(documentFacade);

  useEffect(() => {
    // scroll restoration is handled below
    window.history.scrollRestoration = "manual";
  }, []);

  const [onCoverReady, onSensitivityReady] = completionLatch(2, () => {
    // Since we use browser-side rendering, the scroll position is lost
    // between reloads. So we attempt to restore it manually.
    const scrollPos = Number.parseFloat(
      sessionStorage.getItem("app.aprao.AppraisalReport.scrollPos") ?? "",
    );

    if (Number.isFinite(scrollPos)) {
      window.scrollTo(0, scrollPos);
    }

    window.addEventListener("pagehide", () => {
      sessionStorage.setItem(
        "app.aprao.AppraisalReport.scrollPos",
        `${window.scrollY}`,
      );
    });

    onReady();
  });

  const { msg, effectiveLocale } = useI18n();

  return (
    <NumberUnitsContext
      value={createNumberUnits(
        effectiveLocale,
        project.document.currencyCode,
        project.document.lengthUnitCode,
      )}
    >
      <title>{
        // The children of the <title> component must be a single string of text.
        // cf. https://react.dev/reference/react-dom/components/title#use-variables-in-the-title
        `Aprao - ${reportTitle}`
      }</title>
      <FormattedPdfDocument
        header={reportTitle}
        footer={msg("report.footer")}
        {...others}
      >
        <Page>
          <CoverPage
            reportTitle={reportTitle}
            appraisal={documentFacade}
            mapImageUrl={mapImageUrl}
            logoUrl={logoUrl}
            updatedDate={updatedDate}
            slots={{
              locationMap: project.reportConfiguration.isMapEnabled && (
                <LocationMap mapImageUrl={mapImageUrl} />
              ),
            }}
            onReady={onCoverReady}
          />
        </Page>
        <Page>
          <SummaryFinancial
            appraisal={documentFacade}
            salesTaxConfiguration={taxConfigurationFacade}
            tax={taxFacade}
            residual={residualFacade}
            cashflow={cashflowFacade}
            finance={financeFacade}
            flow={flowFacade}
            otherCostsRelatives={otherCostsRelatives}
          />
        </Page>
        {0 < appraisalDocument.stages.revenue.revenueGroups.length && (
          <Page>
            <RevenueBreakdown
              appraisal={documentFacade}
              salesTaxConfiguration={taxConfigurationFacade}
              tax={taxFacade}
            />
          </Page>
        )}
        {0 < appraisalDocument.stages.purchase.sitePurchases.length && (
          <Page>
            <SitePurchase
              appraisalDocument={documentFacade}
              priceManager={priceManager}
              residual={residualFacade}
            />
          </Page>
        )}
        <Page>
          <BuildCost appraisal={documentFacade} />
        </Page>
        {documentFacade.otherCostsGroups.some(withCostsPredicate) && (
          <Page>
            <OtherCosts
              appraisal={documentFacade}
              otherCostsRelatives={otherCostsRelatives}
            />
          </Page>
        )}
        {0 < residualFacade.lenders.length && (
          <Page>
            <Finance
              appraisal={documentFacade}
              residual={residualFacade}
              cashflow={cashflowFacade}
              finance={financeFacade}
              flow={flowFacade}
            />
          </Page>
        )}
        <Page>
          <ErrorBoundary
            fallback={
              <p
                css={css`
                  text-align: center;
                  margin: 50px 0;
                `}
              >
                Sorry, we could not generate the sensitivity analysis.
              </p>
            }
            onError={onError}
          >
            <SensitivitySection
              appraisal={appraisalDocument}
              salesTaxConfiguration={project.salesTaxConfiguration}
              isOperatedAssetSensitivityAnalysesEnabled={
                isOperatedAssetSensitivityAnalysesEnabled ||
                !envDetails.isUserFacingDeployment
              }
              onReady={onSensitivityReady}
            />
          </ErrorBoundary>
        </Page>
      </FormattedPdfDocument>
    </NumberUnitsContext>
  );
});
